import React, { useState } from 'react';
import classnames from 'classnames';
import { FlexContainer } from '@/components/containers/FlexContainer/FlexContainer';
import HelmetComponent from '@/components/general/Helmet/HelmetComponent';
import { useScrollReveal } from '@/hooks/useScrollReveal.hook';
import { publicAsset } from '@/config/appPaths';
import { programContent, programOptions } from '@/data/staticProgramData';
import ProgramCta from '../components/landing/ProgramCta';
import ProgramCardsCarousel from '../components/landing/ProgramCardsCarousel';
import ProgramOutcomesMarquee from '../components/landing/ProgramOutcomesMarquee';
import ProgramOptions from '../components/landing/ProgramOptions';
import ProgramPhoneVideo from '../components/landing/ProgramPhoneVideo';
import ProgramRegistrationSection from '../components/landing/ProgramRegistrationSection';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '../components/landing/ProgramLanding.scss';

const HomeView = () => {
  const t = programContent;
  const [selectedProgram, setSelectedProgram] = useState('company');
  const activeProgramKey = ['company', 'enroll', 'info'].includes(selectedProgram) ? selectedProgram : 'company';
  const { wrapperRef: programPreviewRef, isVisible: isProgramPreviewVisible } = useScrollReveal({ repeat: true });
  const { wrapperRef: audienceCopyRef, isVisible: isAudienceCopyVisible } = useScrollReveal({ repeat: true });
  const { wrapperRef: experienceCopyRef, isVisible: isExperienceCopyVisible } = useScrollReveal({ repeat: true });
  const { wrapperRef: modulesContentRef, isVisible: isModulesContentVisible } = useScrollReveal({ repeat: true });
  const { wrapperRef: offerCopyRef, isVisible: isOfferCopyVisible } = useScrollReveal({ repeat: true });
  const { wrapperRef: mentorContentRef, isVisible: isMentorContentVisible } = useScrollReveal({ repeat: true });
  const { wrapperRef: faqContentRef, isVisible: isFaqContentVisible } = useScrollReveal({ repeat: true });

  const testimonialCards = [...t.testimonials.items, ...t.testimonials.items];

  return (
    <FlexContainer className="program-landing flex-column">
      <HelmetComponent
        title={t.meta.title}
        description={t.meta.description}
        graphTitle={t.meta.title}
        graphDescription={t.meta.description}
      />

      <section className="program-hero" id="inicio">
        <FlexContainer
          className="program-hero__bg"
          aria-hidden="true"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(8, 14, 28, 0.94) 0%, rgba(8, 14, 28, 0.82) 38%, rgba(12, 23, 49, 0.45) 55%, rgba(0, 80, 120, 0.15) 100%), url(${publicAsset('/basc/hero-4k.png')})`,
          }}
        />
        <FlexContainer className="program-hero__inner flex-column">
          <FlexContainer className="program-hero__copy flex-column align-items-start">
            <span className="program-hero__badge">{t.hero.badge}</span>
            <p className="program-hero__pretitle">{t.hero.pretitle}</p>
            <h1 className="program-hero__title">{t.hero.title}</h1>
            <p className="program-hero__stats program-hero__stats--inline">
              <span className="program-stats-highlight">{t.hero.statsHighlight}</span>
            </p>
            <ProgramCta href="#destacados" variant="accent" className="program-hero__cta">
              {t.hero.scrollCta}
            </ProgramCta>
          </FlexContainer>
        </FlexContainer>
      </section>

      <section
        className="program-feature-bar"
        id="destacados"
        aria-label="Destacados del programa"
      >
        {t.featureBar.items.map((item, index) => (
          <FlexContainer
            className={`program-feature-bar__item program-feature-bar__item--${index + 1} flex-column`}
            key={item.text}
          >
            <i className={`fa-solid ${item.icon} program-feature-bar__icon`} aria-hidden="true" />
            <p className="program-feature-bar__text">{item.text}</p>
          </FlexContainer>
        ))}
      </section>

      <section className="program-section program-section--dark program-section--preview" id="preview">
        <FlexContainer className="program-preview" column>
          <FlexContainer
            className={classnames('program-preview__copy flex-column', {
              'program-scroll-reveal--visible': isProgramPreviewVisible,
            })}
            wrapperRef={programPreviewRef}
          >
            <p className="program-preview__pretitle">{t.programPreview.pretitle}</p>
            <h2 className="program-section-title">{t.programPreview.title}</h2>
            <ul className="program-checklist program-checklist--dark">
              {t.programPreview.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </FlexContainer>
          <FlexContainer className="program-preview__media">
            <ProgramPhoneVideo
              videoUrl={t.programPreview.videoUrl}
              title={t.programPreview.videoTitle}
            />
          </FlexContainer>
        </FlexContainer>
      </section>

      <section className="program-section program-section--audience" id="programa">
        <FlexContainer className="program-audience" column>
          <FlexContainer
            className={classnames('program-audience__copy flex-column', {
              'program-scroll-reveal--visible': isAudienceCopyVisible,
            })}
            wrapperRef={audienceCopyRef}
          >
            <h2 className="program-section-title">{t.audience.title}</h2>
            <p className="program-section-subtitle">{t.audience.subtitle}</p>
            <ul className="program-checklist">
              {t.audience.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <FlexContainer className="program-audience__cta width100">
              <ProgramCta href="#experiencia">{t.audience.scrollCta}</ProgramCta>
            </FlexContainer>
          </FlexContainer>
          <FlexContainer className="program-audience__media">
            <img
              src={publicAsset('/basc/pexels-edwin-stanley-portillo-rodriguez-435388906-35479972.jpg')}
              alt="Buque portacontenedores en vía marítima internacional"
              loading="lazy"
            />
          </FlexContainer>
        </FlexContainer>
      </section>

      <section className="program-section program-section--dark program-section--experience" id="experiencia">
        <FlexContainer className="program-experience" column>
          <FlexContainer className="program-experience__media">
            <img
              src={publicAsset('/basc/pexels-neron-photos-3171798-5864458.jpg')}
              alt="Panorama de la ciudad de Panamá con torres y avenida"
              loading="lazy"
            />
          </FlexContainer>
          <FlexContainer
            className={classnames('program-experience__copy flex-column', {
              'program-scroll-reveal--visible': isExperienceCopyVisible,
            })}
            wrapperRef={experienceCopyRef}
          >
            <h2 className="program-section-title">{t.hero.slogan}</h2>
            <p className="program-section-subtitle">
              {programOptions[activeProgramKey].duration} &middot; {
                activeProgramKey === 'info' ? 'Misión Internacional Presencial' : 'Modalidad híbrida'
              } &middot;
            </p>
            <FlexContainer className="program-experience__cta width100">
              <ProgramCta href="#contenido">{t.experience.scrollCta}</ProgramCta>
            </FlexContainer>
          </FlexContainer>
        </FlexContainer>
      </section>

      <section className="program-section program-section--modules" id="contenido">
        <FlexContainer
          className={classnames('program-modules-content flex-column', {
            'program-scroll-reveal--visible': isModulesContentVisible,
          })}
          wrapperRef={modulesContentRef}
        >
          <h2 className="program-section-title">{t.modules.title}</h2>
          <p className="program-section-subtitle">{programOptions[activeProgramKey].modulesSubtitle}</p>
          <ProgramCardsCarousel items={programOptions[activeProgramKey].moduleIndices.map(index => t.modules.items[index])} variant="modules" />
          <FlexContainer className="program-center-cta width100" justifyCenter>
            <ProgramCta href="#resultados">{t.modules.scrollCta}</ProgramCta>
          </FlexContainer>
        </FlexContainer>
      </section>

      <section className="program-section program-section--dark program-section--outcomes" id="resultados">
        <h2 className="program-section-title">{t.outcomes.title}</h2>
        <ProgramOutcomesMarquee items={t.outcomes.items} />
        <FlexContainer className="program-center-cta width100" justifyCenter>
          <ProgramCta href="#inversion">{t.outcomes.scrollCta}</ProgramCta>
        </FlexContainer>
      </section>

      <section className="program-section program-section--alt program-section--offer" id="inversion">
        <FlexContainer className="program-offer-split" column>
          <FlexContainer
            className={classnames('program-offer-split__copy flex-column', {
              'program-scroll-reveal--visible': isOfferCopyVisible,
            })}
            wrapperRef={offerCopyRef}
          >
            <h2 className="program-section-title">{t.offer.title}</h2>
            <FlexContainer className="program-offer__card flex-column">
              {programOptions[activeProgramKey].offerItems.map((row) => (
                <FlexContainer
                  className="program-offer__row flex-row"
                  justifyBetween
                  alignItemsStart
                  key={row.label}
                >
                  <span className="program-offer__label">{row.label}</span>
                  <span className="program-offer__value">{row.value}</span>
                </FlexContainer>
              ))}
            </FlexContainer>
            <FlexContainer className="program-offer-split__cta width100">
              <ProgramCta href="#modalidades">
                {t.offer.scrollCta}
              </ProgramCta>
            </FlexContainer>
          </FlexContainer>
          <FlexContainer className="program-offer-split__media">
            <img
              src={publicAsset('/basc/pexels-kelly-12530465.jpg')}
              alt="Buque portacontenedores MSC navegando en alta mar"
              loading="lazy"
            />
          </FlexContainer>
        </FlexContainer>
      </section>

      <section className="program-section program-section--dark program-section--options" id="modalidades">
        <FlexContainer
          className={classnames('program-mentor-content flex-column align-items-center', {
            'program-scroll-reveal--visible': isMentorContentVisible,
          })}
          wrapperRef={mentorContentRef}
        >
          <h2 className="program-section-title">{t.modalidades.title}</h2>
          <p className="program-section-subtitle text-center" style={{ maxWidth: '800px', margin: '-10px auto 30px', color: 'rgba(255, 255, 255, 0.7)' }}>
            {t.modalidades.subtitle}
          </p>
          <ProgramOptions selectedOption={selectedProgram} onSelectOption={setSelectedProgram} />
        </FlexContainer>
      </section>

      <section className="program-section program-section--faq" id="faq">
        <FlexContainer
          className={classnames('program-faq-content flex-column', {
            'program-scroll-reveal--visible': isFaqContentVisible,
          })}
          wrapperRef={faqContentRef}
        >
          <h2 className="program-section-title">{t.faq.title}</h2>
          <FlexContainer className="program-faq flex-column">
            {t.faq.items.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </FlexContainer>
        </FlexContainer>
      </section>

      <section className="program-testimonials" id="testimonios">
        <h2 className="program-section-title" style={{ color: '#fff' }}>
          {t.testimonials.title}
        </h2>
        <FlexContainer className="program-testimonials__track flex-row">
          {testimonialCards.map((item, i) => (
            <FlexContainer className="program-testimonials__card flex-column" key={`${item.name}-${i}`}>
              <FlexContainer className="program-testimonials__header flex-row align-items-center">
                <img
                  src={item.photo}
                  alt={item.name}
                  className="program-testimonials__avatar"
                  loading="lazy"
                />
                <FlexContainer className="program-testimonials__author flex-column">
                  <strong>{item.name}</strong>
                  <span>{item.role}</span>
                </FlexContainer>
              </FlexContainer>
              <p>&ldquo;{item.text}&rdquo;</p>
            </FlexContainer>
          ))}
        </FlexContainer>
      </section>

      <ProgramRegistrationSection selectedInterest={activeProgramKey} />

    </FlexContainer>
  );
};

export default HomeView;
